import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Panel, StatusBadge } from "@/components/app/common";
import { EmailChips } from "@/components/sales/EmailChips";
import { useFetch } from "@/hooks/useFetch";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";

const sig = (s, name) => [name, s?.designation, s?.phone && `Mobile: ${s.phone}`, s?.email].filter(Boolean).join("\n");
const body = (q, c) => `Dear ${q.contact_name || "Sir/Madam"},\n\nPlease find attached our quotation ${q.ref} for your kind consideration.\n\nDo let us know if you need any clarification.\n\nRegards,\n${sig(c.sender, c.sender_name)}\nTESSOL`;

export function EmailQuoteDialog({ q, onClose, onSent }) {
  const { data: c } = useFetch(`/quotes/${q.id}/email-contacts`);
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (c && !f) setF({ to: c.customer[0] ? [c.customer[0].email] : [], ccCustomer: [], ccTeam: [], subject: `Quotation ${q.ref} - ${q.company_name_snapshot}`, message: body(q, c) });
  }, [c, f, q]);
  const send = async () => {
    setBusy(true);
    try { await api.post(`/quotes/${q.id}/email`, { to: f.to, cc: [...f.ccCustomer, ...f.ccTeam], subject: f.subject, message: f.message }); toast.success("Quotation emailed"); onSent(); onClose(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="quote-email-dialog">
        <DialogHeader><DialogTitle>Email {q.ref}</DialogTitle></DialogHeader>
        {!f ? <div className="text-sm text-slate-500">Loading contacts…</div> : <div className="space-y-3">
          {!c.configured && <p className="text-xs rounded border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900" data-testid="quote-email-not-configured">Email sending is not set up yet (Gmail service account pending) — the quotation can still be downloaded.</p>}
          {!c.can_send && <p className="text-xs rounded border border-red-200 bg-red-50 px-3 py-2 text-red-800" data-testid="quote-email-not-authorised">{c.block_reason || `Only the quote's sales owner (${c.owner || "not set"}) or an admin in the Sales Team master can send this quotation.`}</p>}
          <div><Label>To (customer) *</Label><EmailChips value={f.to} onChange={(v) => setF({ ...f, to: v })} suggestions={c.customer} placeholder="Type an email and press Enter" testid="quote-email-to" /></div>
          <div><Label>CC — customer side</Label><EmailChips value={f.ccCustomer} onChange={(v) => setF({ ...f, ccCustomer: v })} suggestions={c.customer.filter((x) => !f.to.includes(x.email))} placeholder="Add customer colleagues" testid="quote-email-cc-customer" /></div>
          <div><Label>CC — TESSOL team</Label><EmailChips value={f.ccTeam} onChange={(v) => setF({ ...f, ccTeam: v })} suggestions={c.team.filter((x) => x.email !== c.reply_to)} placeholder="Add colleagues" testid="quote-email-cc-team" /></div>
          <p className="text-[11px] text-slate-500" data-testid="quote-email-auto-cc">Always copied: {[c.sales_inbox, c.reply_to].filter(Boolean).join(", ")} · Replies go to {c.reply_to}</p>
          <div><Label>Subject</Label><Input value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid="quote-email-subject" /></div>
          <div><Label>Message</Label><Textarea rows={8} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="quote-email-message" /></div>
          <p className="text-[11px] text-slate-500">Attachment: the quotation PDF on the TESSOL letterhead.{q.status === "draft" ? " Sending marks this draft as shared." : ""}</p>
        </div>}
        <DialogFooter><Button onClick={send} disabled={!f || !f.to.length || busy || !c?.configured || !c?.can_send} data-testid="quote-email-send"><Mail className="h-4 w-4 mr-1" />{busy ? "Sending…" : "Send email"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function QuoteEmailLog({ q, refreshKey }) {
  const { data, reload } = useFetch(`/quotes/${q.id}/emails`);
  useEffect(() => { reload(); }, [refreshKey, reload]);
  return (
    <Panel title="Emails sent" testid="quote-email-log">
      {(data || []).map((e) => (
        <div key={e.id} className="border-b border-slate-100 py-1.5 text-xs" data-testid="quote-email-log-row">
          <div className="flex items-center gap-2"><StatusBadge status={e.status} /><span className="font-mono">{e.ref}</span><span className="text-slate-500">{fmtDateTime(e.sent_at)} · {e.sent_by?.name}</span></div>
          <div>To: {e.to.join(", ")}</div>
          {e.cc.length > 0 && <div className="text-slate-500">CC: {e.cc.join(", ")}</div>}
          {e.error && <div className="text-red-700">{e.error}</div>}
        </div>
      ))}
      {!(data || []).length && <div className="text-xs text-slate-400">Not emailed yet</div>}
    </Panel>
  );
}
